import { GraftCodePrimitiveType } from '@graftcode/protocol'

import {
  TYPE_USAGE_ARRAY,
  TYPE_USAGE_GENERIC,
  TYPE_USAGE_GENERIC_ALIAS,
  TYPE_USAGE_PRIMITIVE,
} from '../../../const/graftModelPayload.const'
import { UnifiedGraftModelTypeKey } from '../../../const/unifiedGraftModelTypeKeys.const'

import { getEffectiveTypeUsageKind, hasPayload, resolveNode } from './nodeHelpers'
import type { NodeLike, ParameterShape } from './types'

export function classifyTypeUsage(typeUsage: NodeLike | null | undefined): ParameterShape {
  if (!typeUsage || typeof typeUsage !== 'object' || !Array.isArray(typeUsage.payload)) {
    return { kind: 'other' }
  }
  const node = resolveNode(typeUsage)
  if (!node) return { kind: 'other' }

  const kind = getEffectiveTypeUsageKind(node)
  const p = node.payload as unknown[]

  if (kind === UnifiedGraftModelTypeKey.TYPE_USAGE_GENERIC_ALIAS) {
    return {
      kind: 'genericAlias',
      name: (p[TYPE_USAGE_GENERIC_ALIAS.ALIAS_NAME] as string) ?? 'unknown',
    }
  }

  if (kind === UnifiedGraftModelTypeKey.TYPE_USAGE_PRIMITIVE) {
    const codeRaw = p[TYPE_USAGE_PRIMITIVE.PRIMITIVE_TYPE]
    const code = Number(codeRaw)
    const namespace = (p[TYPE_USAGE_PRIMITIVE.NAMESPACE] as string) ?? ''
    const typename = (p[TYPE_USAGE_PRIMITIVE.TYPENAME] as string) ?? ''
    const typeFqn = namespace ? `${namespace}.${typename}` : typename
    return {
      kind: 'primitive',
      primitiveType: Number.isNaN(code) ? undefined : (code as GraftCodePrimitiveType),
      typeFqn,
    }
  }

  if (kind === UnifiedGraftModelTypeKey.TYPE_USAGE_ARRAY) {
    const dimensions = Number(p[TYPE_USAGE_ARRAY.DIMENSION]) || 1
    const elementRaw = p[TYPE_USAGE_ARRAY.ELEMENT_TYPE]
    const elementTypeUsage = hasPayload(elementRaw) ? elementRaw : undefined
    return {
      kind: 'array',
      element: classifyTypeUsage(elementTypeUsage ?? null),
      dimensions,
      elementTypeUsage,
    }
  }

  if (kind === UnifiedGraftModelTypeKey.TYPE_USAGE_GENERIC) {
    const name = (p[TYPE_USAGE_GENERIC.GENERIC_NAME] as string) ?? 'unknown'
    const args = p
      .slice(TYPE_USAGE_GENERIC.TYPE_ARGS_FROM)
      .map((arg) => (hasPayload(arg) ? classifyTypeUsage(arg) : ({ kind: 'other' } as ParameterShape)))
    return { kind: 'generic', name, args }
  }

  return { kind: 'other' }
}
